import { h, Component, Fragment, Prop, Element, State, Listen } from '@stencil/core';
import state, { type State as StoreState, store } from '../../state';

interface Option {
  label: string;
  value: string;
}
export interface WithHTML extends Option {
  html: () => string;
}
export type DropdownOption = Option | (Option & WithHTML);

@Component({
  tag: 'om-dropdown-input',
  styleUrl: './dropdown-input.css',
  scoped: true,
})
export class DropdownInput {
  @Element() el: HTMLElement;

  @Prop() options: DropdownOption[];
  @Prop() handleSelect: (option: DropdownOption) => void;
  @Prop() containerRefKey: keyof StoreState['dropdownContainerRefs'];
  @Prop() placeholder?: string;
  @Prop() initialValue?: DropdownOption['value'];
  @Prop() borderRadius?: string;
  @Prop() fontFamily?: string;
  @Prop() fontSize?: string;

  @State() selectedOption: DropdownOption | null = null;

  private fontStyle() {
    return {
      ...(this.fontFamily ? { fontFamily: this.fontFamily } : {}),
      ...(this.fontSize ? { fontSize: `${this.fontSize}px` } : {}),
    };
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    if (state.openDropdown?.id === this.el?.id) {
      this.updateOptionsPosition();
    }
  }

  private updateOptionsPosition() {
    if (state.openDropdown === null) return;

    const dropdownContainerRect = state.dropdownContainerRefs[this.containerRefKey]?.getBoundingClientRect();

    const maxHeight = 300;
    state.openDropdown = {
      ...state.openDropdown,
      style: {
        maxHeight: `${maxHeight}px`,
        ...(window.innerHeight - dropdownContainerRect.bottom < maxHeight
          ? {
              bottom: `${window.innerHeight - dropdownContainerRect.top}px`,
            }
          : {
              top: `${dropdownContainerRect.bottom}px`,
            }),
        left: `${dropdownContainerRect.left}px`,
        width: `${dropdownContainerRect.width}px`,
        ...this.fontStyle(),
      },
    };
  }

  private openOptions() {
    state.openDropdown = {
      id: this.el.id,
      options: this.options,
      onOptionSelect: (option: DropdownOption) => {
        this.handleSelect(option);
        this.selectedOption = option;
        this.closeOptions();
      },
    };

    state.currentScreenContainer?.classList.add('overflow-hidden');

    setTimeout(() => {
      const listbox = state.shadowRoot.getElementById(`${this.el.id}-options`);
      (listbox?.children?.[0] as HTMLLIElement)?.focus();
    }, 100);

    this.updateOptionsPosition();
  }

  private closeOptions() {
    state.openDropdown = null;
    state.currentScreenContainer?.classList.remove('overflow-hidden');
    state.shadowRoot.getElementById(`${this.el.id}-control`).focus();
  }

  render() {
    const isOpen = state.openDropdown?.id === this.el.id;
    return (
      <Fragment>
        <div class="dropdown-input-container">
          <button
            type="button"
            id={`${this.el.id}-control`}
            role="combobox"
            aria-expanded={isOpen.toString()}
            aria-controls={state.openDropdown?.id}
            aria-label={this.placeholder}
            tabIndex={0}
            class="dropdown-button input"
            style={{
              ...(this.borderRadius ? { borderRadius: `${this.borderRadius}px` } : {}),
              ...this.fontStyle(),
            }}
            onClick={() => {
              if (state.mode === 'preview') {
                return;
              }

              if (isOpen) {
                this.closeOptions();
              } else {
                this.openOptions();
              }
            }}
          >
            <span {...(!this.selectedOption && this.placeholder ? { class: 'placeholder' } : {})}>
              {this.selectedOption?.label || this.placeholder}
            </span>
            <div class={`caret ${isOpen ? 'open' : 'closed'}`}>
              <span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width={1.5}
                  stroke="#000000"
                  width={15}
                  height={15}
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </span>
            </div>
          </button>
        </div>
      </Fragment>
    );
  }

  componentWillLoad() {
    if (this.initialValue) {
      this.selectedOption = this.options.find(option => option.value === this.initialValue) ?? null;
    }
    store.onChange('isMobileViewport', () => {
      state.openDropdown = null;
    });
    store.onChange('currentScreen', () => {
      state.openDropdown = null;
    });
  }
}
